import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService, RealtimeEvent, RealtimeEventPage, RealtimeFeed } from '../services/api';
import { mergeRealtimeEvents } from '../features/realtime/presentation';

const HISTORY_LIMIT = 10;
/** Live history has its own lifetime: searching never clears or replaces it. */
export function useRealtimeFeedHistory(projectId: string, feeds: RealtimeFeed[]) {
  const [eventsByFeed, setEventsByFeed] = useState<Record<string, RealtimeEvent[]>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [revision, setRevision] = useState(0);
  const loaded = useRef(new Set<string>());
  const feedIds = feeds.filter(feed => feed.project_id === projectId).map(feed => feed.feed_id).sort().join(',');
  const mergeEvent = useCallback((event: RealtimeEvent) => {
    if (!event.filter_matched) return;
    setEventsByFeed(current => ({ ...current, [event.feed_id]: mergeRealtimeEvents(current[event.feed_id] || [], [event]).slice(0, HISTORY_LIMIT) }));
  }, []);
  const clear = useCallback(() => { loaded.current.clear(); setEventsByFeed({}); setLoading({}); setErrors({}); }, []);
  const retry = useCallback((feedId: string) => { loaded.current.delete(feedId); setRevision(value => value + 1); }, []);
  useEffect(() => {
    const controller = new AbortController();
    for (const feedId of feedIds.split(',').filter(Boolean)) {
      if (loaded.current.has(feedId)) continue;
      setLoading(current => ({ ...current, [feedId]: true }));
      setErrors(current => ({ ...current, [feedId]: false }));
      void (async () => {
        let cursor: string | null = null;
        let events: RealtimeEvent[] = [];
        // Each server page scans a bounded snapshot. Follow continuation even
        // when another feed occupies the entire scanned page.
        do {
          const page: RealtimeEventPage = await apiService.listRealtimeEvents(projectId, { feed_ids: [feedId], matching_only: true }, cursor, controller.signal);
          if (controller.signal.aborted) return;
          events = mergeRealtimeEvents(events, page.events).slice(0, HISTORY_LIMIT);
          cursor = page.next_cursor;
        } while (cursor && events.length < HISTORY_LIMIT);
        loaded.current.add(feedId);
        setEventsByFeed(current => ({ ...current, [feedId]: mergeRealtimeEvents(events, current[feedId] || []).slice(0, HISTORY_LIMIT) }));
      })().catch(() => {
        if (!controller.signal.aborted) setErrors(current => ({ ...current, [feedId]: true }));
      }).finally(() => {
        if (!controller.signal.aborted) setLoading(current => ({ ...current, [feedId]: false }));
      });
    }
    return () => controller.abort();
  }, [feedIds, projectId, revision]);
  return { eventsByFeed, loading, errors, retry, mergeEvent, clear };
}
